import { useNavigate, useParams } from "react-router-dom";
import "./style.css"
import Screen from "../Layouts/Screen/Screen";
import { Helmet } from "react-helmet-async";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMessage } from "../../global-components/src";
import Spinner, { LoadingThreeCircle } from "../../global-components/src/components/spinner/spiner";
import axios from "axios";
import { useAuth } from "../../global-components/src";
import { BellOff, CheckCircle, MoreVertical, Trash2 } from "react-feather";
import { Notification as NotificationType } from "../../types";
import { calculateTime } from "../../utils/TextManagerTwo";
import ConfirmationModal from "../../components/Modals/ConfirmationModal";
import { BUCKET_PACOCA_URL, PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { clearText } from "../../global-components/src/utils/text";
import { BadgeCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NotificationIcon } from "./notification-icon";
import AnchoredDropdown, { DropdownItem } from "../../components/AnchoredDropdown/AnchoredDropdown";
import { normalizeStoryNotificationLink } from "../../utils/story-notification-link";

const normalizeMomentNotificationText = (type: string, text: string): string => {
    if (type !== "20" && type !== "21") return text;
    return text
        .replaceAll("your instant", "your moment")
        .replaceAll("tu instant", "tu momento")
        .replaceAll("votre instant", "votre moment")
        .replaceAll("dein Instant", "deinen Moment")
        .replaceAll("il tuo instant", "il tuo momento")
        .replaceAll("seu instant", "seu momento")
        .replaceAll("インスタント", "モーメント")
        .replaceAll("instant", "momento")
        .replaceAll("Instant", "Momento");
};

const Notifications = () => {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const endOfListRef = useRef<HTMLDivElement | null>(null);
    const [notifications, setNotifications] = useState<NotificationType[] | null>(null);
    const [notificationsType, setNotificationsType] = useState("all");
    const { setMessage } = useMessage();
    const [loading, setLoading] = useState(true);
    const [end, setEnd] = useState(false);
    const [page, setPage] = useState(() => { return 1; });
    const { token, user } = useAuth();
    const navigate = useNavigate();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [errorModal, setErrorModalPassword] = useState(''); // Adicione um estado para a senha
    const closeRef = useRef<HTMLButtonElement | null>(null);
    const [password, setPassword] = useState(''); // Adicione um estado para a senha
    const [pullDistance, setPullDistance] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const startY = useRef<number | null>(null);
    const startX = useRef<number | null>(null);
    const { t } = useTranslation();
    const [readyToRefresh, setReadyToRefresh] = useState(false);

    const changeNotificationsType = (type: string) => {
        if(loading) return;
        setEnd(false);
        setLoading(false)
        setPage(1); // Mude para 1 para a primeira página
        setNotifications([]);

        // Se o tipo for diferente, atualizar o estado
        if (type !== notificationsType) {
            setNotificationsType(type);
        } else {
            // Se for o mesmo, apenas recarregar os dados
            loadNotifications(1);
        }
    }

    const handleTouchStart = (e: TouchEvent) => {
        // Só começa a detectar refresh se estiver no topo da página
        if (window.scrollY === 0) {
            startY.current = e.touches[0].clientY;
            startX.current = e.touches[0].clientX; // Guarda posição X inicial
        } else {
            startY.current = null;
            startX.current = null;
        }
    };

    const handleTouchMove = (e: TouchEvent) => {
        if (startY.current === null || startX.current === null) return;

        const currentY = e.touches[0].clientY;
        const currentX = e.touches[0].clientX;
    
        // Calcula a distância vertical e horizontal
        const deltaY = currentY - startY.current;
        const deltaX = currentX - startX.current;

        const target = e.target as HTMLElement;
        if (target.closest(".modal") || window.scrollY > 0 || target.closest(".react-modal-sheet-container")) return;

        if (Math.abs(deltaY) > Math.abs(deltaX) * 2) {
        // Só permite arrasto para baixo (refresh)
            if (deltaY > 0) {
                e.preventDefault();
                const pullDistance = Math.min(deltaY * 0.5, 100);
                setPullDistance(pullDistance);
                
                // Define como readyToRefresh somente se passar do threshold
                if (pullDistance > 60 && !refreshing) {
                    setReadyToRefresh(true);
                } else {
                    setReadyToRefresh(false);
                }
            }
        }
    };

    const getTextNotification = (text_secondary?: string) =>{
        if(text_secondary?.includes("https://media")) return "🌇 GIF";
        return clearText(text_secondary?.replace(/[\r\n]+/g, "") ?? "");
    }

    const handleTouchEnd = async () => {
        if (readyToRefresh && !refreshing) {
            setRefreshing(true);
            setReadyToRefresh(false);
            await loadNotifications(1, true);
            setRefreshing(false);
        }
        // setReadyToRefresh(false);
        setPullDistance(0);
            startY.current = null;
        };

        useEffect(() => {
        window.addEventListener("touchstart", handleTouchStart);
        window.addEventListener("touchmove", handleTouchMove);
        window.addEventListener("touchend", handleTouchEnd);

        return () => {
            window.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleTouchEnd);
        };
    }, [readyToRefresh, refreshing]);

    const loadNotifications = async (newPage?: number, reloadData?: boolean) => {
        // Tenta carregar do localStorage antes de tudo
        const localData = localStorage.getItem(`myNotifications-${notificationsType}-${user?.id}`);
        if (localData) {
            try {
                const parsedChats = JSON.parse(localData);
                setNotifications(parsedChats);
            } catch (e) {
                console.warn("Erro ao ler chats locais:", e);
            }
        }

        const currentPage = newPage ?? page + 1; // Usa newPage se fornecido, senão incrementa
        const url = PACOCA_API_URL + `/notifications?page=${currentPage}&typeNotification=${notificationsType}`;

        // if (loading || end) return;
        setLoading(true);

        try {
            const response = await axios.get(url, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = response.data;
            localStorage.removeItem(`myNotifications-${notificationsType}-${user?.id}`);

            if (data.notifications.length > 0) {
                if(reloadData) setNotifications([]);
                
                setNotifications((prevData) => {
                    const existingIds = new Set((prevData || []).map(n => n.id));

                    // Filtra apenas notificações novas (com ID ainda não existente)
                    const newUniqueNotifications = data.notifications.filter((n: NotificationType) => !existingIds.has(n.id));

                    const updated = [...(prevData || []), ...newUniqueNotifications];

                    // Ordena por created_at (decrescente ou crescente, como preferir)
                    updated.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

                    localStorage.setItem(`myNotifications-${notificationsType}-${user?.id}`, JSON.stringify(updated));
                    return updated;
                });
            } else {
                setEnd(true);
            }

        } catch (err: any) {
            if (err.status == 429 && err.message.blocked)
                setMessage(t("generics.errors.tooManyRequests", { minutes: err.remaining }), "error");

            if (err.status == 429)
                setMessage(t("generics.errors.tooManyRequestsGeneric"), "error");

            if (err.response) {
                // O servidor respondeu com um código de erro
                console.error("Erro no servidor:", err.response.status, err.response.data);
                setMessage(err.response.data.message || "Erro ao carregar notificações!", "error");
            } else if (err.request) {
                // A requisição foi feita, mas não houve resposta
                console.error("Nenhuma resposta do servidor:", err.request);
                setMessage(t("generics.errors.genericServerError"), "error");
            } else {
                // Outro tipo de erro (ex: erro de configuração)
                console.error("Erro inesperado:", err.message);
                setMessage("Ocorreu um erro inesperado!", "error");
            }
            console.error(err);

            setEnd(true)
        } finally {
            setLoading(false);
        }
    };

    const openNotification = async (notification: NotificationType) => {
        const url = PACOCA_API_URL + `/notifications/mark-as-read/${notification.id}`;

        if(!notification.opened){
            // Atualiza uma notificação como lida no localStorage
            const current = JSON.parse(localStorage.getItem(`myNotifications-${notificationsType}-${user?.id}`) || "[]");
            const updated = current.map((n: NotificationType) =>
                n.id === notification.id ? { ...n, opened: true } : n
            );
            localStorage.setItem(`myNotifications-${notificationsType}-${user?.id}`, JSON.stringify(updated));

            axios.patch(url,{ id_notification: notification.id },{
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                }
            ).catch(err => console.error("Erro ao marcar como lida:", err)); //adicionar catch para enviar requisição sem await
        }

        const adjustedLink = normalizeStoryNotificationLink(
            adjustNotificationLink(notification.link1),
            notification.type,
        );
        if (/^https?:\/\//i.test(adjustedLink)) {
            window.location.assign(adjustedLink);
            return;
        }

        navigate(adjustedLink);
    }

    // links das notificações (backend cadastra url antiga)
    const adjustNotificationLink = (link: string): string => {
        switch (true) {
            case link.includes("/comments-responses/"):
                return link.replace("/comments-responses/", "/comentarios/respostas/");
            case link.includes("/chat?chat_id="):
                return link.replace("/chat?chat_id=", "/chat/");
            default:
                return link;
        }
    };

    useEffect(() => {
        const modalBody = document.getElementById("modal-comments-body");

        const handleScroll = () => {
            if (!modalBody) return;
            const { scrollTop, clientHeight, scrollHeight } = modalBody;

            if ((scrollTop + clientHeight >= scrollHeight - 50) && !end && !loading) {
                setPage((prevPage) => prevPage + 1); // Agora a página é atualizada antes da requisição
            }
        };

        if (modalBody) {
            modalBody.addEventListener("scroll", handleScroll);
            window.addEventListener("scroll", handleScroll);
        }

        return () => {
            if (modalBody) {
                modalBody.removeEventListener("scroll", handleScroll);
                window.removeEventListener("scroll", handleScroll);
            }
        };
    }, [end, loading]);

    useEffect(() => {
        if(!loading && !end) loadNotifications(page);
    }, [page, notificationsType]);

    useEffect(() => {
        loadNotifications(page);
    }, []);

    const observerCallback = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const target = entries[0];
            if (target.isIntersecting && !loading && !end) {
                // setPage((prev) => prev + 1);
            }
        },
        [loading, end]
    );

    useEffect(() => {
        if (endOfListRef.current) {
            observerRef.current = new IntersectionObserver(observerCallback, {
                root: null,
                rootMargin: "200px",
                threshold: 0.1
            });

            observerRef.current.observe(endOfListRef.current);
        }

        return () => observerRef.current?.disconnect();
    }, [observerCallback]);

    useEffect(() => {
        // Salva a posição de rolagem quando o feed for modificado
        const handleScroll = () => {
            sessionStorage.setItem("scrollPosition", window.scrollY.toString());
        };

        window.addEventListener("scroll", handleScroll)

        return () => {
            window.removeEventListener("scroll", handleScroll)
        };
    }, []);

    const handleClearNotifications = async () => {
        setLoading(true)
        const url = `${PACOCA_API_URL}/notifications`;


        try {
            if(notifications?.length){
                await axios.delete(url, {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                });
            }


            setShowConfirmation(false)
            setMessage(t("notifications.cleared"), "success");
            setNotifications([]);
            localStorage.removeItem(`messagesByChat-${user?.id}`);
            localStorage.removeItem(`myNotifications-all-${user?.id}`);
            localStorage.removeItem(`myNotifications-mention-${user?.id}`);
            localStorage.removeItem(`myNotifications-comment-${user?.id}`);
            localStorage.removeItem(`myNotifications-likePost-${user?.id}`);
            localStorage.removeItem(`myNotifications-likeComment-${user?.id}`);
            localStorage.removeItem(`myNotifications-responseComment-${user?.id}`);
            localStorage.removeItem(`myNotifications-resrepostPost-${user?.id}`);
        } catch (err: any) {
            if (err.response) {
                console.error("Erro no servidor:", err.response.status, err.response.data);
                if (err.response.status === 401) {
                    setErrorModalPassword(t("generics.errors.sessionExpired"))
                }
                else if (err.response.status === 429 && err.response.data.message) {
                    setErrorModalPassword(t("generics.errors.tooManyRequests", { minutes: err.response.data.remaining ?? 5 }))
                }
                else if (err.response.status === 429) {
                    setErrorModalPassword(t("generics.errors.tooManyRequestsGeneric"));
                }
                else if (err.response) {
                    // O servidor respondeu com um código de erro
                    console.error("Erro no servidor:", err.response.status, err.response.data);
                    setErrorModalPassword(err.response.data.message || "Erro ao fazer post!")
                }
            }
            console.error('Erro:', err);
        } finally {
            setLoading(false)
        }
    }

    const markAllAsRead = async () => {
        if (!notifications?.length) return;

        try {
            await axios.patch(`${PACOCA_API_URL}/notifications/mark-all-as-read`, {}, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            });

            setNotifications(current => current?.map(notification => ({ ...notification, opened: true })) ?? current);

            Object.keys(localStorage)
                .filter(key => key.startsWith(`myNotifications-`) && key.endsWith(`-${user?.id}`))
                .forEach(key => {
                    const cached = localStorage.getItem(key);
                    if (!cached) return;
                    try {
                        const parsed = JSON.parse(cached) as NotificationType[];
                        localStorage.setItem(key, JSON.stringify(parsed.map(notification => ({ ...notification, opened: true }))));
                    } catch {
                        // Ignore malformed cached notification data.
                    }
                });
        } catch (err) {
            console.error("Erro ao marcar notificações como lidas:", err);
        }
    };

    const onClose = () => {
        setPassword("");
        setShowConfirmation(false)
    }

    return (
        <div className="notifications-screen">
            <ConfirmationModal
                show={showConfirmation}
                coverMobile={false}
                onClose={() => onClose()}
                onConfirm={() => handleClearNotifications()} // Passa a senha aqui
                title={t("notifications.clearConfirm")}
                setPassword={setPassword} // Adicione a função setPassword aqui
                errorMessage={errorModal}
                isLoading={loading}
                closeRef={closeRef}
            />

            <Screen showMenuIcon={false} title={t("notifications.title")} contentMenu={
                <>
                    {loading || notifications?.length ? (
                        <AnchoredDropdown
                            trigger={<MoreVertical size={20} />}
                            triggerClassName="icon-small-menu-top"
                        >
                            <DropdownItem icon={<CheckCircle size={16} />} onClick={markAllAsRead}>
                                {t("notifications.markAllRead")}
                            </DropdownItem>
                            <DropdownItem icon={<Trash2 size={16} />} danger onClick={() => setShowConfirmation(true)}>
                                {t("notifications.clearConfirm")}
                            </DropdownItem>
                        </AnchoredDropdown>
                    ) : null }
                </>
            }>
                <Helmet>
                    <title>Paçoca - {t("notifications.title")}</title>
                    <meta name="description" content={`Minhas notificações do Paçoca, a rede dos brasileiros!`} />
                    <meta name="keywords" content={`Paçoca, rede social brasileira, Notificações}`} />
                    <meta name="author" content="Paçoca Inc." />
                    <meta property="og:title" content={`Paçoca - Notificações`} />
                    <meta property="og:description" content={`Minhas notificações do Paçoca, a rede dos brasileiros`} />
                    <meta property="og:image" content="/img/link-preview.png?v=1" />
                    <meta property="og:url" content={window.location.href} />
                    <meta property="og:type" content="website" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={`Paçoca - Notificações`} />
                    <meta name="twitter:description" content={`Minhas notificações do Paçoca, a rede dos brasileiros`} />
                    <meta name="twitter:image" content="/img/link-preview.png?v=1" />
                </Helmet>

                <div className="container-search">
                    {refreshing && (
                        <div className="refreshing">
                            <img src="/img/loading.gif" alt="" height={30} />
                        </div>
                    )}

                    {readyToRefresh && (
                        <div className="refreshing">
                            <img src="/img/loading.gif" alt="" height={20} />
                        </div>
                    )}

                    <div className="row row-fillter-notifications">
                        <button className={`btn col-notification btn-light all ${notificationsType == "all" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("all")}>
                            <p>{t("notifications.tabs.all")}</p>
                        </button>
                        <button className={`btn col-notification btn-light mention ${notificationsType == "mention" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("mention")}>
                            <p>{t("notifications.tabs.mentions")}</p>
                        </button>
                        <button className={`btn col-notification btn-light comment ${notificationsType == "comment" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("comment")}>
                            <p>{t("notifications.tabs.comments")}</p>
                        </button>
                        <button className={`btn col-notification btn-light comment ${notificationsType == "viewProfile" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("viewProfile")}>
                            <p>{t("notifications.tabs.viewProfile")}</p>
                        </button>
                        <button className={`btn col-notification btn-light likePost ${notificationsType == "likePost" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("likePost")}>
                            <p>{t("notifications.tabs.likePost")}</p>
                        </button>
                        <button className={`btn col-notification btn-light likeComment ${notificationsType == "likeComment" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("likeComment")}>
                            <p>{t("notifications.tabs.likeComment")}</p>
                        </button>
                        <button className={`btn col-notification btn-light responseComment ${notificationsType == "responseComment" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("responseComment")}>
                            <p>{t("notifications.tabs.responseComment")}</p>
                        </button>
                        <button className={`btn col-notification btn-light repostPost ${notificationsType == "repostPost" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("repostPost")}>
                            <p>{t("notifications.tabs.repostPost")}</p>
                        </button>

                        {/* <button className={`btn col-notification btn-light report ${notificationsType == "report" ? "notification-type-active" : ""}`} onClick={() => changeNotificationsType("report")}>
                            <p>Denúncias</p>
                        </button> */}
                    </div>

                    <>
                    {notificationsType != "viewProfile" || (notificationsType == "viewProfile" && user?.stamps?.includes("blue")) ? (
                        <>
                            {notifications?.map((notification) => {
                                return (
                                    <div onClick={() => openNotification(notification)} key={notification.id} className={`${!notification.opened ? "notification_opened" : ""} row-notification open-notification`}>
                                        <NotificationIcon notification={notification}/>
                                        
                                        <div className="col" style={{ overflow: "hidden" }}>
                                            <h5 className="title-notification" style={{ marginBottom: 0 }}><a className="title-notification">{normalizeMomentNotificationText(notification.type, notification.text)}</a></h5>
                                            <p className="text-notification" dangerouslySetInnerHTML={{__html: getTextNotification(notification.text_secondary) }}></p>
                                            <p>{calculateTime(notification.created_at)}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    ): null}


                        {!user?.stamps?.includes("blue") && notificationsType == "viewProfile" ? (
                            <div className="user-select-none row row-empty-comment" style={{marginLeft: "-40px"}}>
                                <span className="span-empty" style={{ marginLeft: "40px" }}>
                                    <BadgeCheck className="icon-empty" />
                                    <h3 className="w-100 text-center">Seja verificado para visualziar</h3>
                                </span>
                            </div>
                        ) : (
                            <>
                                {!loading && !notifications?.length ? (
                                    <div className="user-select-none row row-empty-comment" style={{marginLeft: "-40px"}}>
                                        <span className="span-empty" style={{ marginLeft: "40px" }}>
                                            <BellOff className="icon-empty" />
                                            <h3 className="w-100 text-center">{t("notifications.empty")}</h3>
                                        </span>
                                    </div>
                                ): null}
                            </>
                        )}


                        <p ref={endOfListRef}></p>
                    </>


                    {loading && (
                        <>
                            {[...Array(1)].map((_, index) => (
                                <div className="notification_opened row-notification open-notification row-user">
                                    <span className="user-select-none col-1 img-account-search img-notification placeholder"/>
                                    <div className="col" style={{ paddingLeft: "5px", overflow: "hidden",  }}>
                                        <h5 className="h5-name-search name-card-search-user" style={{ overflowWrap: "anywhere", whiteSpace: "nowrap", overflow: "hidden", marginBottom: "2px" }}>
                                            <span className="name-search" style={{ marginRight: 5 }}>
                                            <div className="placeholder" style={{height: "10px", width: "90%"}}></div>
                                            </span>
                                        </h5>
                                        <p className="username-search" style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                                            <span className="placeholder mt-1" style={{height: "10px", width: "100px"}}></span>
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </Screen>
        </div>
    );
};

export default Notifications;